import { observer } from 'mobx-react';
import * as React from 'react';
import { CoreCsvExportStore, csvExportStore as defaultCsvExportStore } from '../../../../stores/csv-export';
import { BaseButton } from '../../../base';
import * as S from './csv-export-widget.styles';

const BEFORE_UNLOAD_MESSAGE =
    'A message export is in progress. Leaving or refreshing this page will cancel the export.';

export interface CsvExportWidgetProps {
    csvExportStore?: CoreCsvExportStore;
}

const CsvExportWidgetComponent: React.FC<CsvExportWidgetProps> = ({
    csvExportStore = defaultCsvExportStore,
}) => {
    const { isVisible, isActive, status, title, progressLabel, loaded, total } = csvExportStore;

    React.useEffect(() => {
        if (!isActive) {
            return undefined;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
            event.preventDefault();
            event.returnValue = BEFORE_UNLOAD_MESSAGE;
            return BEFORE_UNLOAD_MESSAGE;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isActive]);

    if (!isVisible) {
        return null;
    }

    const progressPercent = total > 0 ? (loaded / total) * 100 : isActive ? 5 : 100;

    return (
        <S.WidgetContainer role="status" aria-live="polite">
            <S.WidgetHeader>
                <S.WidgetTitleGroup>
                    <S.WidgetTitle>{title}</S.WidgetTitle>
                    <S.WidgetSubtitle>{progressLabel}</S.WidgetSubtitle>
                </S.WidgetTitleGroup>
            </S.WidgetHeader>

            {(isActive || status === 'completed') && (
                <S.ProgressTrack>
                    <S.ProgressBar value={status === 'completed' ? 100 : progressPercent} />
                </S.ProgressTrack>
            )}

            <S.WidgetActions>
                {isActive ? (
                    <BaseButton type="button" variant="warning" size="small" onClick={() => csvExportStore.cancel()}>
                        Cancel
                    </BaseButton>
                ) : (
                    <BaseButton type="button" variant="text" size="small" onClick={() => csvExportStore.dismiss()}>
                        Dismiss
                    </BaseButton>
                )}
            </S.WidgetActions>
        </S.WidgetContainer>
    );
};

export const CsvExportWidget = observer(CsvExportWidgetComponent);
